'use client'

import React, { useEffect, useState, useMemo, useCallback } from 'react'; 
import { useContextState } from "../../context/Context";
// import { useApi } from "../../store/api";
import { HeaderButtonConfig } from '../../models';
import { headerConfigs } from '../../config/headerData';
import { isValidFilterActionType } from '../../context/Reducers';
import {  isValidSocialState } from '../../context/SocialContext'; 
import {  isValidInboxState } from '../../context/InboxContext';

// Component Imports
import SecondHeaderButton from './SecondHeaderButton';

// --- Props Interface (The Core Change) ---
interface Props {
    headerType?: string;
    // Changed the type to be more flexible. It now accepts any function
    // that takes a string and returns nothing. This decouples it from
    // the parent's state management implementation (e.g., useState).
    callBack?: (viewName: string) => void;
}

const SecondHeader: React.FC<Props> = ({ headerType, callBack }) => {
    // --- Hooks and Context ---
    // console.log('header: ', headerType)
    // const { navigate } = useApi();
    const { eventsCTX, socialCTX, inboxCTX, headerCTX, notificationCTX } = useContextState();
    const { eventsDispatch } = eventsCTX;
    const { dispatchPeopleState } = socialCTX;
    const { dispatchInboxState } = inboxCTX;
    const {selectedId, setSelectedId} = headerCTX;

    // --- Component State ---
    // const [selectedId, setSelectedId] = useState<number | null>(null);

    const { 
        privateInboxUnreadMessagesCount, 
        eventsInboxUnreadMessagesCount,
        privateAndEventsInboxUnreadMessagesCount 
    } = notificationCTX;

    // --- Memoized Header Configuration ---
    // This logic is already well-optimized with useMemo.
    const currentHeaderConfig = useMemo(() => {
        const config = headerConfigs[headerType || 'headers'] || headerConfigs.headers || [];

        if (headerType === 'messages') {
            return config.map(item => 
                item.type === 'private' ? { ...item, notificationState: privateInboxUnreadMessagesCount } :
                item.type === 'event' ? { ...item, notificationState: eventsInboxUnreadMessagesCount } :
                item
            );
        }
        if (headerType === 'social') {
            return config.map(item => 
                item.type === '/inbox' ? { ...item, notificationState: privateAndEventsInboxUnreadMessagesCount } : item
            );
        }
        return config;
    // Now, depend on the stable primitive values, not the unstable object.
    }, [headerType, privateInboxUnreadMessagesCount, eventsInboxUnreadMessagesCount, privateAndEventsInboxUnreadMessagesCount]);

    // --- Handler for Button Clicks ---
    const handleHeaderClick = useCallback((header: HeaderButtonConfig) => {
        setSelectedId(header.id);

        switch (header.searchType) {
            case 'peopleState':
                if (isValidSocialState(header.type)) {
                    dispatchPeopleState({ type: 'SET_SOCIAL_STATE', payload: header.type });
                }
                break;
            case 'inboxState':
                if (isValidInboxState(header.type)) {
                    dispatchInboxState({ type: 'SET_INBOX_STATE', payload: header.type });
                }
                break;
            case 'state':
                callBack?.(header.type);
                break;
            default:
                if (isValidFilterActionType(header.searchType)) {
                    // console.log('header: ', header.type)
                    eventsDispatch({ type: header.searchType, payload: header.type });
                }
                break;
        }
    // Define the dependencies for this callback.
    }, [ dispatchPeopleState, dispatchInboxState, callBack, eventsDispatch]);

    // --- Effect for Initial Selection ---
    // This effect runs once to set the initial selected button.
    useEffect(() => {
        if (currentHeaderConfig.length > 0) {
            const initialHeader = currentHeaderConfig[0];
            // Don't navigate automatically, just set the state for the first button
            handleHeaderClick(initialHeader);
        }
    // We only want this to run when the config itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentHeaderConfig]);

    // Dynamically set justification class based on number of items
    // const justificationClass = currentHeaderConfig.length < 5 ? 'justify-center' : 'sm:justify-center justify-start ';
    // console.log('header: ', currentHeaderConfig[0])
    const justificationClass = useMemo(()=>{
        switch (currentHeaderConfig[0].headerType) {
            case 'cache':
                return 'justify-center '
            case 'event':
                return 'sm:justify-center justify-start '
            case 'inbox':
                return 'justify-center '
            case 'messages':
                return 'justify-center '
            default:
                return 'sm:justify-center justify-start '
        }
    },[])

    // console.log('test: ', currentHeaderConfig)

    return (
        <div className="sticky top-20 z-10 bg-white">
            <div className={`flex lg:flex-1 pl-3 pr-3 rounded-md shadow-md space-x-2 overflow-x-auto w-full ${justificationClass}`}>
                {currentHeaderConfig.map((header) => (
                    <button
                        className="group block flex-shrink-0 p-2 focus:outline-none focus:bg-gray-100"
                        key={header.id}
                        onClick={() => handleHeaderClick(header)}
                        style={{ backgroundColor: header.id === selectedId ? "#E5E7EB" : "white" }} // Use hex for gray-200 for consistency
                    >
                        <SecondHeaderButton button1={header} key={header.id} />
                    </button>
                ))}
            </div>
        </div>
    );
};

export default SecondHeader;